import { createContext, useContext } from 'react';
import type { DropTarget } from './moveTargets';

/**
 * What a row needs in order to take part in dragging.
 *
 * `ref` goes on the row element; `handleProps` carry the listeners that start a
 * drag; `isOver` and `refusing` decide how the row is drawn while something
 * hovers it.
 */
export interface RowDragState {
  /**
   * Named `setNodeRef` rather than `ref`: a property literally called `ref` is
   * read by the React Compiler lint as a React ref object, which this is not —
   * it is dnd-kit's node-registration callback.
   */
  setNodeRef?: (element: HTMLElement | null) => void;
  handleProps?: Record<string, unknown>;
  isDragging: boolean;
  /** Something is hovering this row and it would accept the drop. */
  isOver: boolean;
  /** Something is hovering this row and it would not (FR-030). */
  refusing: boolean;
}

const INERT: RowDragState = {
  isDragging: false,
  isOver: false,
  refusing: false,
};

/**
 * Named `useRowDraggable`/`useRowDroppable` rather than mirroring dnd-kit's own
 * `useDraggable`/`useDroppable`: `scripts/check-bundle.sh` detects the library
 * by grepping built chunks for those exact API names, and identifiers that
 * merely contain them would report this module as shipping dnd-kit when it does
 * not import a line of it.
 */
export interface RowDragBinding {
  useRowDraggable: (path: string) => RowDragState;
  useRowDroppable: (target: DropTarget | undefined) => RowDragState;
}

/**
 * The inert binding, in play whenever the drag layer is not mounted — tests
 * that render the listing directly, and any surface that never drags.
 *
 * It calls no hooks, and which implementation is in play never changes during a
 * mount: the layer renders its children only once its chunk has resolved, so a
 * consumer sees the real binding from its very first render. Hook order is
 * therefore stable either way.
 */
const INERT_BINDING: RowDragBinding = {
  useRowDraggable: () => INERT,
  useRowDroppable: () => INERT,
};

/**
 * Supplies dragging to the listing without the listing importing `@dnd-kit`.
 *
 * That indirection is the point: importing `@dnd-kit` outside a lazy boundary
 * puts it in the entry bundle, which `pnpm check:bundle` fails and the
 * constitution lists as a merge blocker. Only `FileDragLayer` — reachable only
 * through `FileDragLayerLazy` — ever touches the library.
 */
export const FileDragContext = createContext<RowDragBinding>(INERT_BINDING);

export function useRowDragBinding(): RowDragBinding {
  return useContext(FileDragContext);
}
